import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { doc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { UserContext } from '../contexts/UserContext';
import { isEligibleForTips, nextTip, stepsForMember } from '../utils/onboardingTips';

const IS_DEV = process.env.NODE_ENV === 'development';
const FIRST_TIP_DELAY_MS = 1200;

/**
 * Which first-visit tip to show on this page right now.
 * `steps`: [{ id, ready? }] in display order (see utils/onboardingTips.js).
 * Returns { activeTip, dismiss(id) }.
 */
export default function useOnboardingTips(steps) {
  const { firestoreUser } = useContext(UserContext);
  const [localDismissed, setLocalDismissed] = useState({});

  // Localhost review helper: ?resetTips=1 clears your own dismissals.
  useEffect(() => {
    if (!IS_DEV) return;
    const uid = auth.currentUser && auth.currentUser.uid;
    if (!uid || !new URLSearchParams(window.location.search).has('resetTips')) return;
    updateDoc(doc(db, 'users', uid), { onboardingTips: deleteField() })
      .then(() => setLocalDismissed({}))
      .catch((err) => console.warn('resetTips failed:', err));
  }, [firestoreUser ? true : false]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissed = useMemo(
    () => ({ ...((firestoreUser && firestoreUser.onboardingTips) || {}), ...localDismissed }),
    [firestoreUser, localDismissed]
  );

  const eligible = isEligibleForTips(firestoreUser);
  const activeTip = eligible ? nextTip(stepsForMember(steps, firestoreUser), dismissed) : null;

  // The first tip a page shows waits a moment so the page can load and the
  // member can see where they are; tips after a "Got it" follow straight on
  // (Jason, 2026-10-20).
  const firstTipRef = useRef(null);
  if (activeTip && firstTipRef.current === null) firstTipRef.current = activeTip;
  const delay = activeTip && activeTip === firstTipRef.current ? FIRST_TIP_DELAY_MS : 0;

  const dismiss = useCallback((id) => {
    setLocalDismissed((prev) => ({ ...prev, [id]: true }));
    const uid = auth.currentUser && auth.currentUser.uid;
    if (!uid) return;
    updateDoc(doc(db, 'users', uid), { [`onboardingTips.${id}`]: serverTimestamp() })
      .catch((err) => console.warn('Could not record tip dismissal:', err));
  }, []);

  return { activeTip, dismiss, delay };
}
